/**
 * Team Validation Schemas
 */

import { z } from 'zod';
import { nameField, optionalBoundedTextOrEmpty } from './shared';

/**
 * Common validation rules for Team entities
 */
export const teamValidationRules = {
  /** Team name validation (1-200 characters) */
  name: nameField(200, 'Team name is required', 'Team name must be less than 200 characters'),

  /** Description validation (optional, max 2000 characters) */
  description: optionalBoundedTextOrEmpty(2000, 'Description must be less than 2000 characters'),

  /** Parent team ID validation (must start with "team-") */
  parentTeamId: z
    .union([z.string().regex(/^team-/, 'Parent Team ID must start with "team-"'), z.literal('')])
    .optional(),

  /** Lead user ID validation (required string) */
  leadUserId: z.string().min(1, 'Team lead is required'),

  /** Member IDs validation (array of strings) */
  memberIds: z.array(z.string().min(1)).optional().default([]),

  /** Responsibilities validation (array of strings) */
  responsibilities: z.array(z.string().min(1).max(500)).optional().default([]),

  /** Contact email validation */
  contactEmail: z.string().email('Invalid email address'),

  /** Teams channel URL validation (optional, must be a Microsoft Teams URL) */
  teamsChannel: z
    .union([
      z
        .string()
        .regex(
          /^https:\/\/teams\.microsoft\.com\//,
          'Teams channel must be a valid Microsoft Teams URL'
        ),
      z.literal(''),
    ])
    .optional(),
};

/**
 * Full Team create form schema
 */
export const createTeamSchema = z.object({
  name: teamValidationRules.name,
  description: teamValidationRules.description,
  type: z.enum(['development', 'operations', 'platform', 'security', 'data'], {
    message: 'Team type is required',
  }),
  parentTeamId: teamValidationRules.parentTeamId,
  leadUserId: teamValidationRules.leadUserId,
  memberIds: teamValidationRules.memberIds,
  responsibilities: teamValidationRules.responsibilities,
  contactEmail: teamValidationRules.contactEmail,
  teamsChannel: teamValidationRules.teamsChannel,
});

/**
 * Team update form schema (all fields optional except at least one must be present)
 */
export const updateTeamSchema = z
  .object({
    name: teamValidationRules.name.optional(),
    description: teamValidationRules.description,
    type: z.enum(['development', 'operations', 'platform', 'security', 'data']).optional(),
    parentTeamId: teamValidationRules.parentTeamId,
    leadUserId: teamValidationRules.leadUserId.optional(),
    memberIds: z.array(z.string().min(1)).optional(),
    responsibilities: z.array(z.string().min(1).max(500)).optional(),
    contactEmail: teamValidationRules.contactEmail.optional(),
    teamsChannel: teamValidationRules.teamsChannel,
  })
  .refine((data) => Object.values(data).some((value) => value !== undefined), {
    message: 'At least one field must be updated',
  });

/**
 * Type inference for create team form
 */
export type CreateTeamFormData = z.infer<typeof createTeamSchema>;

/**
 * Type inference for update team form
 */
export type UpdateTeamFormData = z.infer<typeof updateTeamSchema>;
